import { Close, MagnifyingGlass } from '@liveauctioneers/hammer-ui-core/icons';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import React, { ChangeEvent, ReactNode, useCallback, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useDebouncedCallback } from 'use-debounce';

import { ModifyQueryParamsAction, useModifyQueryParams } from '@/hooks/queryParams/useModifyQueryParams';

import { PaginationFilter } from './types/PaginationFilter';

export type SearchFilterProps = {
    readonly className?: string;
    readonly onChange: Function;
    /** A PaginationFilter object  */
    readonly paginationFilter: PaginationFilter;
    /** Used to display result count, or any text you want really  */
    readonly resultsDisplay?: any;
    readonly searchLabel?: ReactNode;
    /** Text to display in the search bar when nothing is entered */
    readonly searchPlaceholder?: string;
    /**
     * Reset `page` to 1 in the URL whenever a keyword is entered. Opt-in: consumers that filter the
     * whole collection server-side need this (searching from a later page would otherwise request
     * that page of the smaller filtered set and render empty), while consumers that page
     * independently of search should leave it off.
     */
    readonly shouldResetPageOnChange?: boolean;
    /** Should modify query params */
    readonly shouldModifyQueryParams?: boolean;
    readonly testid?: string;
};

export const SearchFilter = ({
    className,
    onChange,
    paginationFilter,
    resultsDisplay,
    searchLabel,
    searchPlaceholder,
    shouldModifyQueryParams,
    shouldResetPageOnChange,
    testid = 'searchFilter',
}: SearchFilterProps) => {
    const modifyQueryParams = useModifyQueryParams();

    const [search, setSearch] = useState(paginationFilter.keyword);

    const onSearchChange = useCallback(
        (filters?: { keyword: string; page: number }) => {
            if (shouldModifyQueryParams) {
                if (filters?.keyword === '') {
                    modifyQueryParams({
                        action: ModifyQueryParamsAction.REMOVE,
                        params: 'keyword',
                    });
                } else {
                    modifyQueryParams({
                        action: ModifyQueryParamsAction.ADD,
                        // A single ADD merges into the current params, so clearing `page` here (not
                        // just the `page: 1` handed to `onChange`, which never reaches the URL)
                        // resets the pager in the same navigation as the keyword. `undefined` drops
                        // the param — page 1 carries no `page` in the URL, the same shape the pager
                        // itself writes when it lands on page 1.
                        query: shouldResetPageOnChange
                            ? { keyword: filters?.keyword, page: undefined }
                            : { keyword: filters?.keyword },
                    });
                }
            }
            onChange(filters);
        },
        [shouldModifyQueryParams, shouldResetPageOnChange, onChange, modifyQueryParams]
    );
    const debouncedSearchChanged = useDebouncedCallback(onSearchChange, 300);

    const handleSearchChanged = useCallback(
        ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
            const { value } = currentTarget;
            setSearch(value);
            debouncedSearchChanged({ keyword: value, page: 1 });
        },
        [debouncedSearchChanged]
    );

    const handleSearchClear = useCallback(() => {
        setSearch('');
        onSearchChange({ keyword: '', page: 1 });
    }, [onSearchChange]);

    return (
        <div
            className={cn('flex flex-col', {
                'gap-xs': resultsDisplay !== undefined,
            })}
        >
            <TextInput
                aria-label="Search Filter"
                className={className}
                data-testid={testid}
                labelText={searchLabel}
                onChange={handleSearchChanged}
                placeholder={searchPlaceholder}
                small
                suffix={
                    paginationFilter.keyword ? (
                        <Close
                            className="cursor-pointer text-link-primary"
                            onClick={handleSearchClear}
                            size="sm"
                        />
                    ) : (
                        <MagnifyingGlass
                            className="self-center"
                            size="sm"
                        />
                    )
                }
                value={search}
            />
            {resultsDisplay}
        </div>
    );
};

export default React.memo<SearchFilterProps>(SearchFilter, isEqual);
